/**
 * ②利用状況（08 §5.1・FR-72）＝テナント別の日次注文数の推移。
 *
 * 「使っていない卸」も 0 埋めで並ぶ（サーバが billable_tenants を 0 で返す）＝
 * 稼働しているのに注文ゼロの卸を運営が見つけられる。原文には踏み込まない（件数のみ）。
 */

import { ops, type OpsUsage, type OpsUsageTenant } from "@kenzai/api-client";
import { Card, Empty, ErrorNote, formatDate, PageHeader, Skeleton, Table, Td } from "@kenzai/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

const DAY_CHOICES = [7, 14, 30] as const;

export function Usage() {
  const [days, setDays] = useState<number>(14);
  const q = useQuery({
    queryKey: ["ops-usage", days],
    queryFn: () => ops.usage({ days }),
  });

  return (
    <>
      <PageHeader
        title="利用状況"
        subtitle="テナントごとの日次注文数です（デモは除外）。"
        actions={
          // M1.7＝**セグメントタブ**（くぼんだ台の上で選択中だけが白く浮く）。
          // ★`aria-pressed` は必ず残す（`e2e/ops-monitoring.spec.ts:82` が直接検証している）。
          <div className="kz-tabs" role="group" aria-label="期間">
            {DAY_CHOICES.map((n) => (
              <button
                key={n}
                type="button"
                className={days === n ? "kz-tab is-selected" : "kz-tab"}
                data-testid={`usage-days-${n}`}
                aria-pressed={days === n}
                onClick={() => setDays(n)}
              >
                {n}日
              </button>
            ))}
          </div>
        }
      />
      {q.isLoading ? (
        // ロード中に `<table>` を置かない（素の帯だけ）＝E2E が「表＝データ確定」を前提にしている
        <Card>
          <Skeleton rows={6} height={18} />
        </Card>
      ) : q.isError ? (
        <ErrorNote error={q.error} />
      ) : (q.data?.tenants.length ?? 0) === 0 ? (
        <Empty title="テナントがありません" hint="開設した卸がここに並びます。" />
      ) : (
        <UsageBody data={q.data!} />
      )}
    </>
  );
}

function UsageBody({ data }: { data: OpsUsage }) {
  const first = data.days[0];
  const last = data.days[data.days.length - 1];
  // 全テナント通しの最大値でスパークラインの縦軸を揃える（卸どうしを見比べられる）。
  const peak = Math.max(1, ...data.tenants.flatMap((t) => t.counts));
  return (
    <div style={{ display: "grid", gap: "var(--kz-space-3)" }}>
      <p style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)", margin: 0 }}>
        期間：<span className="kz-num">{formatDate(first)}</span> 〜{" "}
        <span className="kz-num">{formatDate(last)}</span>
      </p>
      <Card padding="0">
        <Table head={["コード", "名称", "推移", "合計", "今日"]}>
          {data.tenants.map((t) => (
            <UsageRow key={t.tenant_id} t={t} peak={peak} />
          ))}
        </Table>
      </Card>
    </div>
  );
}

function UsageRow({ t, peak }: { t: OpsUsageTenant; peak: number }) {
  const today = t.counts[t.counts.length - 1] ?? 0;
  return (
    <tr data-testid={`usage-row-${t.tenant_id}`}>
      <Td>
        <span className="kz-num">{t.code}</span>
      </Td>
      <Td style={{ fontWeight: 600 }}>{t.name}</Td>
      <Td>
        <Sparkline counts={t.counts} peak={peak} />
      </Td>
      <Td style={{ whiteSpace: "nowrap" }}>
        <span className="kz-num" data-testid={`usage-total-${t.tenant_id}`} style={{ fontWeight: 700 }}>
          {t.total}
        </span>
      </Td>
      <Td style={{ whiteSpace: "nowrap" }}>
        <span className="kz-num">{today}</span>
      </Td>
    </tr>
  );
}

/** 日次件数の棒スパークライン（値ラベルは出さず「推移の形」だけ・SVG で自己完結）。 */
function Sparkline({ counts, peak }: { counts: number[]; peak: number }) {
  const w = 132;
  const h = 28;
  const gap = 1;
  const bw = counts.length > 0 ? (w - gap * (counts.length - 1)) / counts.length : w;
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label={`日次の注文数（最大 ${peak}）`}
      style={{ display: "block" }}
    >
      {counts.map((c, i) => {
        const bh = c === 0 ? 1 : Math.max(2, Math.round((c / peak) * h));
        return (
          <rect
            key={i}
            x={i * (bw + gap)}
            y={h - bh}
            width={bw}
            height={bh}
            rx={0.5}
            fill={c === 0 ? "var(--kz-color-border)" : "var(--kz-color-primary)"}
          />
        );
      })}
    </svg>
  );
}
